import { useState, useRef, useEffect } from 'react';
import {
  SfcShellAppBar,
  SdfCard,
  SdfCardHeader,
  SdfCardFooter,
  SdfBox,
  SdfInput,
  SdfButton,
  SdfTabGroup,
  SdfTab,
  SdfBusyIndicator,
  SdfSelectSimple
} from '@waypoint/react-components';
import DataWebhook from './components/DataWebhook';
import SubscriptionWebhook from './components/SubscriptionWebhook';
import axios from 'axios';

const LOCALE_COOKIE_KEY = 'ADPLangLocaleCookie';
const DEFAULT_LOCALE = 'en-US';
const LOCALE_OPTIONS = [
  { label: 'English (US)', value: 'en-US' },
  { label: 'Français (Canada)', value: 'fr-CA' }
];

const getLocaleFromCookie = () => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE_KEY}=([^;]*)`));

  if (!match) {
    return null;
  }

  // Cookie may be stored as en_US / en-US, and is matched case-insensitively against supported locales.
  const cookieLocale = decodeURIComponent(match[1]).replace('_', '-');

  return LOCALE_OPTIONS.find((option) => option.value.toLowerCase() === cookieLocale.toLowerCase())?.value || null;
};

const resolveInitialLocale = () => getLocaleFromCookie() || DEFAULT_LOCALE;

function App() {
  const [carId, setCarId] = useState('');
  const [showCard,] = useState(true);
  const [mfeKey, setMfeKey] = useState(0);
  const [selectedTab, setSelectedTab] = useState(0);
  const [webhookPing, setWebhookPing] = useState(false);
  const [isPingLoading, setIsPingLoading] = useState(false);
  const [locale, setLocale] = useState(resolveInitialLocale);
  const inputRef = useRef(null);

  const handleSubmit = () => {
    console.log('Submitted CAR ID:', inputRef.current.value);
    setCarId(inputRef.current.value.trim());
    setMfeKey(crypto.randomUUID());
  };

  const webhookPingCall = async () => {
    // if (import.meta.env.VITE_ENV === 'DEV') {
    //   setWebhookPing(true);
    //   return;
    // }

    try {
      setIsPingLoading(true);

      await axios.get(import.meta.env.VITE_WEBHOOK_PING_API_URL, {
        withCredentials: true
      });
      setWebhookPing(true);
    } catch (error) {
      setWebhookPing(false);
      console.error('Error pinging webhook:', error);
    }

    setIsPingLoading(false);
  };

  useEffect(() => {
    document.title = `${import.meta.env.VITE_ENV} Standalone Webhook UI`;
    webhookPingCall();
    setLocale(() => resolveInitialLocale());
  }, []);

  return (
    <>
      <SfcShellAppBar size='small' slot='app-bar'>
        | Standalone Webhook UI
      </SfcShellAppBar>

      {webhookPing && <section
        id="carid-section"
        className={`${showCard ? 'flex' : 'hidden'} justify-center items-center mb-4 mt-12 h-full p-8`}
      >

        <SdfCard spacing="loose">
          <SdfCardHeader>Consumer Application ID</SdfCardHeader>
          <SdfCardFooter>
            <section className="space-y-4">
              <SdfBox variant="clear" spacing="default" style={{ fontSize: '16px' }}>
                Enter valid Consumer Application ID to get the valid data from Embeddable Webhook UI,
                Otherwise the API calls might display various errors.
              </SdfBox>
            </section>
          </SdfCardFooter>
          <SdfInput
            id="carid-input"
            style={{ marginBottom: '0.6em' }}
            value={carId}
            label="Consumer Application Registry (CAR) ID:"
            onChange={(e) => setCarId(e.target.value)}
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <SdfButton id="submit-button" onClick={handleSubmit}>Submit</SdfButton>
          </div>
        </SdfCard>
      </section>}

      {webhookPing && <section className="flex items-center justify-left h-full p-4">
        <SdfTabGroup className="w-full" data-demo-name="tab-group" align="">
          <SdfTab active={selectedTab === 0} data-id="demo-component" data-demo-name="tab1" onClick={() => setSelectedTab(0)}>Data Webhook</SdfTab>
          <SdfTab active={selectedTab === 1} data-id="demo-component" data-demo-name="tab2" onClick={() => setSelectedTab(1)}>Subscription Webhook</SdfTab>
        </SdfTabGroup>
      </section>}

      {webhookPing && (selectedTab === 0 ?
        <DataWebhook mfeKey={mfeKey} carId={carId} locale={locale} />
        :
        <SubscriptionWebhook mfeKey={mfeKey} carId={carId} locale={locale} />
      )}

      {/* If not signed in to DSS or APIC */}
      {!webhookPing &&
        <section className="flex items-center justify-center h-full mt-8 p-4">
          <SdfCard variant="clear" spacing="default" className="p-4" style={{ fontSize: '16px' }}>
            <h1>Please login to DSS or APIC to use Standalone Webhook UI</h1>
            <p>Developer Self Service &#40;DSS&#41;: <a href={import.meta.env.VITE_DSS_URL} target='_blank' rel="noopener noreferrer">{import.meta.env.VITE_DSS_URL}</a></p>
            <p>API Central &#40;APIC&#41;: <a href={import.meta.env.VITE_APIC_URL} target='_blank' rel="noopener noreferrer">{import.meta.env.VITE_APIC_URL}</a></p>
          </SdfCard>
        </section>
      }

      {isPingLoading && <div>
        <br />
        <SdfBusyIndicator size="xl" overlay={true} />
      </div>}
    </>
  );
}

export default App;
